/**
 * <CheckoutAddressModal />
 *
 * Collects a structured shipping address before kicking off Stripe
 * Checkout. Used by ProductDetail's Buy-now flow.
 */
import * as React from "react";
import { Button } from "./Button";
import { Input } from "./Input";
import { Modal } from "./Modal";

export interface ShippingAddressValue {
  recipient_name: string;
  phone: string;
  line1: string;
  line2: string;
  city: string;
  state: string;
  postal_code: string;
  country: string;
}

const BLANK: ShippingAddressValue = {
  recipient_name: "",
  phone: "",
  line1: "",
  line2: "",
  city: "",
  state: "",
  postal_code: "",
  country: "US",
};

interface Props {
  open: boolean;
  onClose: () => void;
  /** Called with the validated address when the user clicks "Continue to payment". */
  onSubmit: (addr: ShippingAddressValue) => Promise<void> | void;
  busy?: boolean;
  initial?: Partial<ShippingAddressValue>;
}

export function CheckoutAddressModal({
  open,
  onClose,
  onSubmit,
  busy,
  initial,
}: Props) {
  const [form, setForm] = React.useState<ShippingAddressValue>({
    ...BLANK,
    ...(initial ?? {}),
  });
  const [error, setError] = React.useState<string | null>(null);

  const set = (k: keyof ShippingAddressValue) =>
    (e: React.ChangeEvent<HTMLInputElement>) =>
      setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    const required: (keyof ShippingAddressValue)[] = [
      "line1",
      "city",
      "state",
      "postal_code",
      "country",
    ];
    for (const k of required) {
      if (!form[k].trim()) {
        setError(`"${k.replace("_", " ")}" is required.`);
        return;
      }
    }
    if (form.country.trim().length !== 2) {
      setError("Country must be a 2-letter code (e.g. US).");
      return;
    }
    await onSubmit({
      ...form,
      country: form.country.trim().toUpperCase(),
    });
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Where should we ship it?"
      description="We'll use this address for your order and the shipping label."
      testid="checkout-address-modal"
    >
      <form onSubmit={submit} className="flex flex-col gap-3" data-testid="checkout-address-form">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Input
            label="Full name"
            value={form.recipient_name}
            onChange={set("recipient_name")}
            autoComplete="name"
            data-testid="address-recipient"
          />
          <Input
            label="Phone (optional)"
            value={form.phone}
            onChange={set("phone")}
            autoComplete="tel"
            data-testid="address-phone"
          />
        </div>
        <Input
          label="Street address"
          required
          value={form.line1}
          onChange={set("line1")}
          autoComplete="address-line1"
          data-testid="address-line1"
        />
        <Input
          label="Apt / Unit (optional)"
          value={form.line2}
          onChange={set("line2")}
          autoComplete="address-line2"
          data-testid="address-line2"
        />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="col-span-2">
            <Input
              label="City"
              required
              value={form.city}
              onChange={set("city")}
              autoComplete="address-level2"
              data-testid="address-city"
            />
          </div>
          <Input
            label="State"
            required
            value={form.state}
            onChange={set("state")}
            autoComplete="address-level1"
            data-testid="address-state"
          />
          <Input
            label="ZIP / postal code"
            required
            value={form.postal_code}
            onChange={set("postal_code")}
            autoComplete="postal-code"
            data-testid="address-postal"
          />
        </div>
        <Input
          label="Country code"
          required
          maxLength={2}
          value={form.country}
          onChange={set("country")}
          hint="Two-letter ISO code (US, CA, UK…)"
          data-testid="address-country"
        />

        {error && (
          <p
            role="alert"
            className="text-sm font-semibold text-brand"
            data-testid="checkout-address-error"
          >
            {error}
          </p>
        )}

        <div className="flex items-center justify-end gap-2 pt-2">
          <Button type="button" variant="outline" size="md" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            size="md"
            loading={busy}
            data-testid="checkout-address-submit"
          >
            Continue to payment
          </Button>
        </div>
      </form>
    </Modal>
  );
}
